import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function CheckoutSuccess() {
  const [state, setState] = useState("checking"); // checking | success | failed | timeout | error
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`
        );
        if (res.status === 404) {
          setState("error");
          return;
        }
        if (!res.ok) throw new Error("status check failed");
        const data = await res.json();
        if (data.payment_status === "paid") {
          setDetails(data);
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="App">
      <Navbar />
      <section className="bg-[#F3F4F6] min-h-[70vh] flex items-center py-20">
        <div className="max-w-2xl mx-auto px-6 w-full">
          <div
            data-testid="checkout-result"
            className="brutal-border brutal-shadow bg-white p-8 sm:p-12 text-center"
          >
            {state === "checking" && (
              <div data-testid="checkout-checking">
                <Loader2 size={56} className="mx-auto text-[#3E6FA8] animate-spin" />
                <h1 className="font-display text-3xl sm:text-4xl mt-6 text-[#1F2530]">
                  Confirming your payment…
                </h1>
                <p className="mt-3 text-[#1F2530]/70 font-medium">
                  Please wait a moment while we verify your transaction.
                </p>
              </div>
            )}

            {state === "success" && (
              <div data-testid="checkout-success">
                <CheckCircle2 size={56} className="mx-auto text-green-600" />
                <h1 className="font-display text-3xl sm:text-4xl mt-6 text-[#1F2530]">
                  Payment Successful!
                </h1>
                <p className="mt-3 text-[#1F2530]/70 font-medium">
                  Thank you for your order{details?.service ? ` — ${details.service}` : ""}.
                  We'll be in touch shortly to get started.
                </p>
                {details?.is_deposit && (
                  <p className="mt-4 text-sm font-semibold italic text-red-600">
                    *A 50% deposit was charged. The remaining balance is due upon order
                    completion. Your order will be delivered once full payment is received.
                  </p>
                )}
                {details?.amount_total != null && (
                  <p className="mt-5 inline-block text-lg font-extrabold bg-[#EAF1F8] text-[#3E6FA8] px-4 py-2 rounded-full">
                    Paid: ${(details.amount_total / 100).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {(state === "failed" || state === "error") && (
              <div data-testid="checkout-failed">
                <XCircle size={56} className="mx-auto text-red-600" />
                <h1 className="font-display text-3xl sm:text-4xl mt-6 text-[#1F2530]">
                  Payment Not Completed
                </h1>
                <p className="mt-3 text-[#1F2530]/70 font-medium">
                  Your payment wasn't completed. You can try again from the order page.
                </p>
              </div>
            )}

            {state === "timeout" && (
              <div data-testid="checkout-timeout">
                <Loader2 size={56} className="mx-auto text-[#3E6FA8]" />
                <h1 className="font-display text-3xl sm:text-4xl mt-6 text-[#1F2530]">
                  Still Processing
                </h1>
                <p className="mt-3 text-[#1F2530]/70 font-medium">
                  Your payment is taking longer than expected. If you completed checkout,
                  you'll receive a confirmation email shortly.
                </p>
              </div>
            )}

            <Link
              to="/contact"
              data-testid="checkout-back"
              className="mt-8 inline-flex items-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-6 py-3"
            >
              <ArrowLeft size={18} strokeWidth={2.5} /> Back to Order
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
